import React, { useState, useEffect } from 'react'
import { Stack, Button, Input, SimpleModal, FormControl, DateChooser, SearchableSelect, Switch, Wrap, WrapItem, Tag, Heading, Select } from '@/components/lib'
import api from '../../../api'
import utils from '../../../utils/utils'

export default function PackForm ({ trialId, packType, pack, sites = [], groups = [], onClose, onCreated, onUpdated }) {
  const isEdit = !!pack
  const [name, setName] = useState(pack?.name ?? '')
  const [batchNumber, setBatchNumber] = useState(pack?.batchNumber ?? '')
  const [assignedToSite, setAssignedToSite] = useState(pack?.assignedToSite ?? null)
  const [availableToAllGroups, setAvailableToAllGroups] = useState(pack?.availableToAllGroups ?? true)
  const [availableToGroups, setAvailableToGroups] = useState(pack?.availableToGroups ? [...pack.availableToGroups] : [])
  const [extended, setExtended] = useState(pack?.extended ?? {})
  const [saving, setSaving] = useState(false)

  const extendedFields = packType?.extendedFields ?? []

  useEffect(() => {
    if (pack?.extended) setExtended({ ...pack.extended })
    else if (extendedFields.length) {
      const init = {}
      extendedFields.forEach((f) => { init[f.key] = '' })
      setExtended(init)
    }
  }, [pack?._id, packType?._id])

  useEffect(() => {
    if (pack) {
      setAssignedToSite(pack.assignedToSite ?? null)
      setAvailableToAllGroups(pack.availableToAllGroups ?? false)
      setAvailableToGroups(pack.availableToGroups ? [...pack.availableToGroups] : [])
    }
  }, [pack?._id])

  function setExtendedValue (key, value) {
    setExtended((prev) => ({ ...prev, [key]: value }))
  }

  function save () {
    setSaving(true)
    const baseData = {
      name: name.trim() || null,
      batchNumber: batchNumber.trim() || null,
      extended: Object.keys(extended).length ? extended : undefined,
      assignedToSite: assignedToSite || null,
      availableToAllGroups,
      availableToGroups: availableToAllGroups ? [] : availableToGroups
    }
    const data = isEdit ? baseData : { packType: packType._id, ...baseData }
    if (isEdit) {
      api.packs.updatePack(pack._id, data, (updated) => {
        setSaving(false)
        onUpdated(updated)
      }, (err) => {
        setSaving(false)
        utils.toast('error', err.message)
      })
    } else {
      api.packs.createPack(trialId, data, (created) => {
        setSaving(false)
        onCreated(created)
      }, (err) => {
        setSaving(false)
        utils.toast('error', err.message)
      })
    }
  }

  return (
    <SimpleModal
      size='md'
      isOpen
      onClose={onClose}
      header={isEdit ? 'Edit pack' : 'Create pack'}
      body={
        <Stack gap={5}>
          <FormControl label='Name'>
            <Input value={name} onChange={(e) => setName(e.target.value)} placeholder='Pack name' />
          </FormControl>
          <FormControl label='Batch number'>
            <Input value={batchNumber} onChange={(e) => setBatchNumber(e.target.value)} placeholder='Optional' />
          </FormControl>
          <FormControl label='Assigned to site'>
            <SearchableSelect
              size='sm'
              placeholder='No site (unassigned)'
              options={[{ value: '', label: 'No site' }, ...sites.map((s) => ({ value: s._id, label: s.name || s.id || s._id }))]}
              value={assignedToSite || ''}
              onChange={(v) => setAssignedToSite(v || null)}
            />
          </FormControl>

          <Switch
            isChecked={availableToAllGroups}
            onChange={(e) => setAvailableToAllGroups(e.checked)}
          >
            Do not restrict to groups
          </Switch>

          {!availableToAllGroups && (
            <FormControl label='Available to specific groups only'>
              <Stack gap={2}>
                <SearchableSelect
                  size='sm'
                  placeholder='Add a group…'
                  options={groups.filter((g) => !availableToGroups.includes(g._id)).map((g) => ({ value: g._id, label: g.name || g._id }))}
                  value=''
                  onChange={(v) => v && setAvailableToGroups((prev) => (prev.includes(v) ? prev : [...prev, v]))}
                />
                {availableToGroups.length > 0 && (
                  <Wrap gap={2}>
                    {availableToGroups.map((groupId) => {
                      const g = groups.find((x) => x._id === groupId)
                      return (
                        <WrapItem key={groupId}>
                          <Tag onClose={() => setAvailableToGroups((prev) => prev.filter((id) => id !== groupId))}>
                            {g?.name || groupId}
                          </Tag>
                        </WrapItem>
                      )
                    })}
                  </Wrap>
                )}
              </Stack>
            </FormControl>
          )}
          {extendedFields.length > 0 && (
            <Stack spacing={5} p={2} rounded='md' bg='bg.subtle'>
              <Heading size='sm'>Additional fields</Heading>
              <Stack gap={2} mt={1}>
                {extendedFields.map((f) => (
                  <FormControl key={f.key} label={f.label}>
                    {f.type === 'boolean'
                      ? (
                        <Select
                          value={extended[f.key] === true || extended[f.key] === 'true' ? 'true' : 'false'}
                          onChange={(e) => setExtendedValue(f.key, e.target.value === 'true')}
                        >
                          <option value='false'>No</option>
                          <option value='true'>Yes</option>
                        </Select>
                        )
                      : f.type === 'date'
                        ? (
                          <Stack direction='row' alignItems='center' gap={2}>
                            <DateChooser
                              size='sm'
                              value={extended[f.key] || null}
                              onChange={(date) => setExtendedValue(f.key, date ? date.toISOString() : null)}
                            />
                            {extended[f.key] && <Button size='sm' variant='ghost' onClick={() => setExtendedValue(f.key, null)}>Clear</Button>}
                          </Stack>
                          )
                        : f.type === 'datetime'
                          ? (
                            <Stack direction='row' alignItems='center' gap={2}>
                              <DateChooser
                                size='sm'
                                withTime
                                value={extended[f.key] || null}
                                onChange={(date) => setExtendedValue(f.key, date ? date.toISOString() : null)}
                              />
                              {extended[f.key] && <Button size='sm' variant='ghost' onClick={() => setExtendedValue(f.key, null)}>Clear</Button>}
                            </Stack>
                            )
                          : (
                            <Input
                              type={f.type === 'integer' || f.type === 'float' ? 'number' : 'text'}
                              value={extended[f.key] ?? ''}
                              onChange={(e) => setExtendedValue(f.key, e.target.value)}
                              placeholder={f.label}
                            />
                            )}
                  </FormControl>
                ))}
              </Stack>
            </Stack>
          )}
        </Stack>
      }
      footer={
        <Stack direction='row' justifyContent='end' gap={2}>
          <Button onClick={onClose}>Cancel</Button>
          <Button colorPalette='primary' onClick={save} disabled={saving}>
            {saving ? 'Saving…' : isEdit ? 'Save' : 'Create'}
          </Button>
        </Stack>
      }
    />
  )
}
